import React from "react";

type AlertType = "success" | "error" | "warning" | "info" | "loading" | "neutral";
type WidthType = "full" | "fit";

interface DashboardAlertBoxProps {
  type?: AlertType;
  width?: WidthType;
  label: string;
  className?: string;
}

const cn = (...classes: Array<string | undefined | false | null>) =>
  classes.filter(Boolean).join(" ");

const typeStyles: Record<AlertType, string> = {
  success: "bg-green-25 border-green-secondary",
  error: "bg-red-25 border-red-secondary",
  warning: "bg-orange-25 border-orange-secondary",
  info: "bg-info-primary border-info-secondary",
  loading: "bg-brown-50 border-brown-secondary",
  neutral: "bg-[rgba(0,0,0,0.04)] border-[rgba(0,0,0,0.5)]",
};

const iconColorClasses: Record<AlertType, string> = {
  success: "text-green-secondary",
  error: "text-red-secondary",
  warning: "text-orange-secondary",
  info: "text-info-secondary",
  loading: "text-brown-secondary",
  neutral: "text-black-text",
};

const iconClasses: Record<AlertType, string> = {
  success: "icon-check",
  error: "icon-triangle-alert",
  warning: "icon-circle-alert",
  info: "icon-info",
  loading: "icon-loader animate-spin",
  neutral: "icon-info",
};

export const DashboardAlertBox: React.FC<DashboardAlertBoxProps> = (
  {
    type = "info",
    width = "full",
    label,
    className,
  }) => {
  const colorClasses = typeStyles[type];
  const iconClass = iconClasses[type];
  const iconColorClass = iconColorClasses[type];
  const widthClass = width === "full" ? "w-full" : "w-fit";

  return (
    <div
      role={type === "error" ? "alert" : "status"}
      aria-live={type === "error" ? "assertive" : "polite"}
      className={cn(
        "flex flex-row items-center gap-3 border px-4 py-3 text-base rounded",
        widthClass,
        colorClasses,
        className
      )}
    >
      <span
        className={cn(iconClass, iconColorClass, "!text-3xl p-0.5 -my-0.5")}
        aria-hidden="true"
      />
      <span className="flex-1">{label}</span>
    </div>
  );
};
